/* eslint-disable @typescript-eslint/no-explicit-any */
import { get_k_sso_token } from './device-id.ts'
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
  AxiosProgressEvent,
} from 'axios'
import axios from 'axios'
import { cloneDeep, get, set } from 'lodash-es'
import router from '@/router.tsx'
import { usePaymentStore } from '@/modules/payment/payment-store'
import { useAuthStore } from '@/stores/auth-store'
import { usePermissionStore } from '@/stores/permission-store'

// 防止重复显示登录过期提示
let isShowingLoginExpiredMessage = false
export interface ConstructParams extends CreateAxiosDefaults {
  /**
   * 用来判断 response 是不是错误
   * @example
   * ```ts
   * isError: (response: AxiosResponse) => response.data.code !== 200
   * ```
   */
  isError?: (response: AxiosResponse) => boolean | Record<string, JsonValue> | undefined
  /**
   * 用来获取业务数据
   * 建议尽量获取全面的数据，不要只获取 data 而忽略 code
   * @example
   * ```ts
   * getData: (response: AxiosResponse) => response.data // 不推荐 response.data.data
   * ```
   */
  getData?: <T>(response: AxiosResponse<T>) => T
  /**
   * 请求拦截器
   */
  requestInterceptor?: [
    ((config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig) | null,
    ((error: unknown) => Promise<unknown>) | null,
  ]
  /**
   * 响应拦截器
   */
  responseInterceptor?: [
    ((response: AxiosResponse) => AxiosResponse) | null,
    ((error: unknown) => Promise<unknown>) | null,
  ]
}
export class HttpClient {
  instance: AxiosInstance
  isError: ConstructParams['isError']
  getData: Exclude<ConstructParams['getData'], undefined> = response => response.data
  requestInterceptor: ConstructParams['requestInterceptor']
  responseInterceptor: ConstructParams['responseInterceptor']
  constructor(params: ConstructParams) {
    const { isError, getData, requestInterceptor, responseInterceptor, ...rest } = params
    this.instance = axios.create(rest)
    this.isError = isError
    this.getData = getData || this.getData
    this.requestInterceptor = requestInterceptor
    this.responseInterceptor = responseInterceptor
    this.intercept()
  }

  private intercept() {
    this.instance.interceptors.response.use((response: AxiosResponse) => {
      const error = this.isError ? this.isError(response) : false
      if (error === true) {
        return Promise.reject({ response })
      }
      if (error instanceof Object) {
        return Promise.reject(error)
      }
      return response
    })
    if (this.requestInterceptor) {
      this.instance.interceptors.request.use(...this.requestInterceptor)
    }
    if (this.responseInterceptor) {
      this.instance.interceptors.response.use(...this.responseInterceptor)
    }
    this.instance.interceptors.response.use(null, (err: AxiosError) => {
      if ('response' in err) {
        const response = err.response as AxiosResponse
        const data = response?.data as { code?: number; err_msg?: string; message?: string } | undefined
        const is404Err = (data?.err_msg?.indexOf('code: 404') ?? -1) >= 0
        if (is404Err && data) {
          (data as Record<string, unknown>).code = 404
        } else if (data?.code === 600) {
          ElMessage.warning(data?.err_msg || data?.message || 'Insufficient balance')
          usePaymentStore().openPurchaseModal()
        } else if (data?.code !== undefined && data?.code !== 401 && data?.code !== 200) {
          ElMessage.error(data?.err_msg || data?.message || 'Unknown error')
        } else if (response?.status === 404 || response?.status === 500) {
          const fallbackMsg = response?.status === 404 ? '请求的资源不存在(404)' : '服务器错误，请稍后重试(500)'
          ElMessage.error(data?.err_msg ?? data?.message ?? fallbackMsg)
        }
      }
      const data = err.response?.data as { code: number } | undefined
      if (data?.code === 401) {
        // if (confirmed) { return }
        // const yes = window.confirm('登录过期，或是用户名密码错误')
        // if (!yes) {
        //   confirmed = false
        //   return
        // }
        // if (yes) {
        //   confirmed = true
        // }
        if (!isShowingLoginExpiredMessage) {
          isShowingLoginExpiredMessage = true
          // 清除认证和权限状态
          try {
            const authStore = useAuthStore()
            const permissionStore = usePermissionStore()
            authStore.clearAuth()
            permissionStore.clearPermissions()
          } catch (error) {
            // Fallback: 清除旧的 token
            localStorage.removeItem('k_sso_token')
            console.error('Failed to clear stores:', error)
          }

          const pathname = window.location.pathname
          // 协议相关路由不做重定向判断
          const isPublicPage = pathname.includes('/terms') || pathname.includes('/privacy') || pathname.includes('/activity')

          if (!isPublicPage) {
            // 非协议页才跳转登录页
            setTimeout(async () => {
              const returnTo = pathname + window.location.search
              try {
                await router.push({
                  path: '/login',
                  query: { returnTo },
                })
              } catch (error) {
                console.error('Router push failed, using location.href:', error)
                window.location.href = `${window.location.origin}/login?returnTo=${encodeURIComponent(returnTo)}`
              }
              isShowingLoginExpiredMessage = false
            }, 300)
          } else {
            isShowingLoginExpiredMessage = false
          }
        }
      }
      return Promise.reject(err)
    })
  }

  /**
   * 发送 GET 请求
   * @param url 请求地址
   * @param params 查询参数
   * @param config 请求配置
   * @returns 请求结果
   * @example
   * ```ts
   * httpClient.get<ServerResponse<User>>("/api/user", { id: 1 })
   * ```
   */
  get<T = unknown>(
    url: string,
    params?: HttpRequestConfig['params'],
    config = {} as Omit<HttpRequestConfig, 'params'>,
  ) {
    return this._request<T>({ url, params, ...config, method: 'GET' })
  }

  /**
   * 发送 POST 请求
   * @param url 请求地址
   * @param data 请求数据
   * @param config 请求配置
   * @returns 请求结果
   * @example
   * ```ts
   * httpClient.post<ServerResponse<User>>("/api/user", { id: 1 })
   * ```
   */
  post<T = unknown>(url: string, data?: any, config: HttpRequestConfig = {}) {
    // post统一增加studio_id参数，后面增加一个白名单，排除一些url
    const whiteList = [
      '/account/sms_code',
      '/account/phone_login',
      '/account/logout',
      '/account/studio_enter_login',
    ]
    try {
      // 只对 /motioncomic 开头的路由注入 studio_id
      if (data && data.studio_id === undefined && !whiteList.some(o => url.includes(o))) {
        try {
          const permissionStore = usePermissionStore()
          if (permissionStore.config?.studio_id !== undefined) {
            data.studio_id = permissionStore.config.studio_id
          }
        } catch (error) {
          // Fallback: 从旧存储读取
          const userMenuConfig = localStorage.getItem('userMenuConfig')
          if (userMenuConfig) {
            const menuConfig = JSON.parse(userMenuConfig)
            data.studio_id = menuConfig?.studio_id ?? 0
          }
        }
      }
    } catch (error) {
      console.error('Failed to inject studio_id:', error)
    }
    return this._request<T>({ url, data, ...config, method: 'POST' })
  }

  /**
   * 发送 PUT 请求
   */
  put<T = unknown>(url: string, data?: JsonValue, config: HttpRequestConfig = {}) {
    return this._request<T>({ url, data, ...config, method: 'PUT' })
  }

  /**
   * 发送 DELETE 请求
   */
  delete<T = unknown>(
    url: string,
    params?: HttpRequestConfig['params'],
    config = {} as Omit<HttpRequestConfig, 'params'>,
  ) {
    return this._request<T>({ url, params, ...config })
  }

  _request<T = unknown>(config: HttpRequestConfig) {
    const excludeUrl = [
      '/backend',
      '/upload/sign', '/series_resource_v2', '/drama', '/api', '/series_deploy/labels',
      '/material/episode_merge_status',
      '/series_resource/material_cutter_download',
      '/material/task_list_series_desc',
      '/material/query_list_by_resource_name',
      '/material/query_list_by_drama_name',
      '/material/deploy_calendar',
      '/series_deploy/task/employees',
      '/series_deploy/calendar',
    ]
    const isFreeCommercial = location.pathname.includes('free-commercial')
    const isExcludeUrl = excludeUrl.some(url => config.url?.includes(url))
    config.url = isFreeCommercial && !isExcludeUrl ? '/fr' + config.url : config.url
    if (config.transformRequestData) {
      const data = cloneDeep(config.data)
      Object.entries(config.transformRequestData).forEach(([key, fnList]) => {
        fnList.forEach(fn => {
          try {
            const value = get(data, key)
            set(data, key, fn(value, data))
          } catch (error) {
            console.error('transformRequestData error:', error)
          }
        })
      })
      config.data = data
    }
    return this.instance.request<T>(config).then(this.getData).then(data => {
      if (config.transformResponseData) {
        const obj = cloneDeep(data) as Record<string, unknown>
        Object.entries(config.transformResponseData).forEach(([key, fnList]) => {
          fnList.forEach(fn => {
            try {
              const value = get(obj, key)
              set(obj, key, fn(value, obj))
            } catch (error) {
              console.error('transformResponseData error:', error)
            }
          })
        })
        return obj as typeof data
      }
      return data
    })
  }
}

export type HttpRequestConfig = AxiosRequestConfig & {
  transformRequestData?: Record<string, Array<(value: any, data: any) => any>>
  transformResponseData?: Record<string, Array<(value: any, data: any) => any>>
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
}

const commonRequestInterceptor: HttpClient['requestInterceptor'] = [
  config => {
    // 获取当前语言设置
    const locale = localStorage.getItem('app-locale')
    const language = locale === 'zh-cn' ? 'zh-CN' : 'en'

    // 从 auth store 读取 token，fallback 到旧的方法
    let token: string = ''
    try {
      const authStore = useAuthStore()
      token = authStore.token || get_k_sso_token() || ''
    } catch (error) {
      token = get_k_sso_token() || ''
    }

    Object.assign(config.headers, {
      device: 'Web',
      token: token,
      language: language,
    })
    return config
  },
  null,
]

export const baseURL = import.meta.env.VITE_API_BASE_URL as string

const commonParams: ConstructParams = {
  // 默认isDomestic是false
  baseURL: baseURL,
  timeout: 60000,
  withCredentials: true,
  isError: response => ![200, 0].includes(response.data.code),
  getData: <T>(response: AxiosResponse<T>) => (response?.data ?? {}) as T,
  requestInterceptor: commonRequestInterceptor,
}

/**
 * 如果以下配置不满足你的需求，你可以修改，或者创建其他 httpClient
 */
export const httpClient = new HttpClient(commonParams)

export const keepError = (fn?: (err: any) => void) => (err: any) => {
  fn?.(err)
  throw err
}
